import type { Batch } from '@ctcells/contract'

/**
 * **시작 전 시약 용량 확인**(정본 `07` §4.4 ③ · `IFC-RO-32` 시약 보충 기록) — 순수 함수뿐이다(그리기 · 요청 없음).
 *
 * 흐름(정본 ④ 「시약 보충 기록 → Pre-flight 다시 → 시작」) — 작업 줄 [시작] → 이 창 → 보충 기록 N건 → `useStartGate.begin()`.
 *
 * 🔴 **기본값은 잰 값이 아니다.** `Batch.reagent_basis` 는 그 배치가 고정한 레시피 판의 `loadLiquid.volumeByWell` 이고
 *    (`basis: 'recipe_assumed'`), 사람이 [확인]하면 그 값 · 고치면 고친 값으로 **모듈 장부를 맞춘다**(계약 설명 · `RCP-REAGENT-03`).
 *    시약 양의 권위는 모듈 장부다 — 이 창은 그 출발점을 사람이 확인하는 자리다.
 * 🔴 **대상 모듈을 고르지 않는다** — `Batch.reagent_module_id` 하나다(서버가 「활성 염색 모듈이 정확히 하나」일 때만 준다).
 * 🔴 **묶음 규칙은 서버 관리 화면과 같다**(`ctcells-core` `app/resources/admin/reagent_bodies.js` `reagentBodies`) —
 *    「웰마다 양이 다르면 나눠 보낸다」(계약 `ReagentReplenishment`) · 시약 줄 차례 · 줄 안에서는 그 양이 처음 나온 웰 차례.
 * ⚠ 서버 관리 화면은 잘못 적은 칸을 «건너뛰고» 보내지만 여기서는 **막는다** — 시작 직전이라, 조용히 빠진 웰이 그대로
 *   시약 소진 보류(`reagent_exhausted`)로 이어진다(dev2 2026-10-08 L 이송 보상 정지 세 번).
 */

export interface ReagentWellDraft {
  well: string
  /** 칸에 적힌 글자 그대로 — 수로 바꾸는 것은 `parseVolume` 이 한다 */
  text: string
}

export interface ReagentLineDraft {
  /** 시약 이름(서버 값 그대로) */
  reagent: string
  /** 리저버 — `null` 이면 장부 키가 없어 기록할 수 없다(아래 `unplaced`) */
  labwareId: string | null
  wells: ReagentWellDraft[]
}

/** 보충 기록 한 건의 본문 재료(`ReagentReplenishment`) — `command_id` · `note` 는 보내는 쪽이 싣는다 */
export interface ReagentBody {
  labwareId: string
  wellNames: string[]
  /** 보충 «후» 웰마다 양(µL) — 더하기가 아니라 맞춤 */
  volume: number
}

export interface ReagentPlan {
  bodies: ReagentBody[]
  /** 수가 아니거나 0 미만이라 보낼 수 없는 칸 — 하나라도 있으면 [확인하고 시작]이 비활성이다 */
  invalid: { line: number; reagent: string; well: string }[]
  /** 리저버를 몰라 기록하지 않는 시약 이름 — 막지 않고 창에 그 사실을 적는다 */
  unplaced: string[]
}

/** `Batch.reagent_basis` → 창의 칸. 기본값 = 레시피 표준 충전량(서버 값 그대로 · 웰 차례도 그대로) */
export function reagentDrafts(basis: Batch['reagent_basis']): ReagentLineDraft[] {
  return basis.map((entry) => {
    const labwareId =
      typeof entry.labwareId === 'string' && entry.labwareId !== '' ? entry.labwareId : null
    return {
      reagent: entry.reagent,
      labwareId,
      wells: Object.entries(entry.volumeByWell).map(([well, volume]) => ({
        well,
        text: String(volume),
      })),
    }
  })
}

/** 칸 글자 → µL. 비었거나 수가 아니거나 0 미만이면 `undefined`(서버 `volume: ge=0`) */
export function parseVolume(text: string): number | undefined {
  const trimmed = text.trim()
  if (trimmed === '') return undefined
  const value = Number(trimmed)
  return Number.isFinite(value) && value >= 0 ? value : undefined
}

/** 칸 → 보낼 본문 목록 · 막는 칸 · 기록 못 하는 시약 */
export function reagentPlan(lines: readonly ReagentLineDraft[]): ReagentPlan {
  const bodies: ReagentBody[] = []
  const invalid: ReagentPlan['invalid'] = []
  const unplaced: string[] = []
  for (const [index, line] of lines.entries()) {
    if (line.labwareId === null) {
      unplaced.push(line.reagent)
      continue
    }
    const groups = new Map<number, string[]>()
    for (const { well, text } of line.wells) {
      const volume = parseVolume(text)
      if (volume === undefined) {
        // 줄 번호로 가른다 — 같은 이름의 시약이 다른 리저버에 또 있을 수 있다(실물 레시피 `PBS-T` 둘)
        invalid.push({ line: index, reagent: line.reagent, well })
        continue
      }
      groups.set(volume, [...(groups.get(volume) ?? []), well])
    }
    for (const [volume, wellNames] of groups)
      bodies.push({ labwareId: line.labwareId, wellNames, volume })
  }
  return { bodies, invalid, unplaced }
}

/** 이 배치가 [시작] 앞에서 시약 확인 창을 거치는가 — 레시피 판에 시약이 1건이라도 있으면 */
export function needsReagentConfirm(batch: Pick<Batch, 'reagent_basis'> | undefined): boolean {
  return (batch?.reagent_basis.length ?? 0) > 0
}
